import { useEffect, useRef } from 'react';

import { logger } from '~shared/logger';

import { findOrCreateWuid } from '@breeze/web-lib-wuid';

interface FingerprintParams {
  apiUrl: string;
  userId: string;
  wuid: string;
}

declare global {
  interface Window {
    initializeAnyId?: (params: FingerprintParams) => Promise<void>;
  }
}

interface UseFingerprintInitParams {
  apiUrl: string;
  userId: string | null;
  isAuthenticated: boolean;
}

export const useFingerprintInit = ({
  apiUrl,
  userId,
  isAuthenticated,
}: UseFingerprintInitParams) => {
  const initializedUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !userId || initializedUserRef.current === userId) {
      return;
    }

    if (typeof window.initializeAnyId !== 'function') {
      logger.warn({
        message: 'Fingerprint script not loaded',
        context: { userId },
      });
      return;
    }

    (async () => {
      try {
        const wuid = findOrCreateWuid();
        await window.initializeAnyId!({ apiUrl, userId, wuid });

        initializedUserRef.current = userId;
        logger.info({
          message: 'Fingerprint initialized',
          context: { userId, wuid },
        });
      } catch (error) {
        logger.error(
          error instanceof Error
            ? error
            : new Error('Fingerprint initialization failed'),
          `Failed for userId: ${userId}`
        );
      }
    })();
  }, [apiUrl, userId, isAuthenticated]);
};
